import is from '@sindresorhus/is';
import type { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { difference } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import type { JobLevelDetails } from '~/shared/data-contract/job-level-details';
import type { JobRoleDetails } from '~/shared/data-contract/job-role-details';
import type { LanguageCode } from '~/shared/internationalization';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { isGenerationInProgress, isTranslating } from '../hooks/use-is-generating';
import { jobArchitectureKeys } from './query-keys';

export type JobItemDetails = JobLevelDetails | JobRoleDetails;

export const getJobItemDetailsRefetchInterval = (
    generationInfo: JobItemDetails['profileGenerationInfo'],
): 1000 | false => (isGenerationInProgress(generationInfo) ? 1000 : false);

export function patchMissingTranslations(
    item: JobLevelDetails,
    availableLanguages: readonly LanguageCode[],
): JobLevelDetails;
export function patchMissingTranslations(
    item: JobRoleDetails,
    availableLanguages: readonly LanguageCode[],
): JobRoleDetails;
export function patchMissingTranslations(
    item: JobItemDetails,
    availableLanguages: readonly LanguageCode[],
): JobItemDetails;
export function patchMissingTranslations(
    item: JobItemDetails,
    availableLanguages: readonly LanguageCode[],
): JobItemDetails {
    const missingLanguages = difference(
        availableLanguages,
        item.translations.map((translation) => translation.language),
    );
    const isGenerating = isTranslating(item.profileGenerationInfo);

    const patchedJobProfile = is.undefined(item.jobProfile)
        ? undefined
        : {
              ...item.jobProfile,
              translations: [
                  ...item.jobProfile.translations,
                  ...missingLanguages.filter((_) => isGenerating).map((language) => ({ language, title: '' })),
              ],
          };

    return {
        ...item,
        jobProfile: patchedJobProfile,
        translations: [
            ...item.translations,
            ...missingLanguages.filter((_) => isGenerating).map((language) => ({ language, title: '' })),
        ],
    };
}

export type JobItemDetailsRequest = {
    readonly enabled?: boolean;
    readonly itemId: string;
    readonly version: string | undefined;
};

const createJobItemDetailsQueryOptions = ({
    enabled,
    itemId,
    queryFn,
    queryKey,
}: {
    readonly enabled: boolean;
    readonly itemId: string;
    readonly queryFn: ({ signal }: { readonly signal: AbortSignal }) => Promise<JobItemDetails>;
    readonly queryKey: QueryKey;
}) =>
    queryOptions({
        enabled,
        notifyOnChangeProps: ['data', 'error', 'isFetching', 'isLoading'],
        placeholderData: (previousData) => {
            return previousData?.id === itemId ? previousData : undefined;
        },
        queryFn,
        queryKey,
        refetchInterval: (query) => {
            const generationInfo = query.state.data?.profileGenerationInfo;

            return getJobItemDetailsRefetchInterval(generationInfo);
        },
        staleTime: 60_000,
        throwOnError: false,
    });

type JobItemDetailsListQueryInput<TDetails extends JobItemDetails> = {
    readonly enabled: boolean;
    readonly itemId: string;
    readonly placeholderData: TDetails | undefined;
    readonly queryFn: ({ signal }: { readonly signal: AbortSignal }) => Promise<TDetails>;
    readonly queryKey: QueryKey;
};

// react-query cannot infer placeholderData through a generic, so each item type gets a concrete builder.
const createJobLevelDetailsListQueryOptions = ({
    enabled,
    itemId,
    placeholderData,
    queryFn,
    queryKey,
}: JobItemDetailsListQueryInput<JobLevelDetails>) =>
    queryOptions({
        enabled,
        notifyOnChangeProps: ['data', 'error', 'isFetching', 'isLoading'],
        placeholderData: (previousData) => (previousData?.id === itemId ? previousData : placeholderData),
        queryFn,
        queryKey,
        refetchInterval: (query) => getJobItemDetailsRefetchInterval(query.state.data?.profileGenerationInfo),
        staleTime: 60_000,
        throwOnError: false,
    });

const createJobRoleDetailsListQueryOptions = ({
    enabled,
    itemId,
    placeholderData,
    queryFn,
    queryKey,
}: JobItemDetailsListQueryInput<JobRoleDetails>) =>
    queryOptions({
        enabled,
        notifyOnChangeProps: ['data', 'error', 'isFetching', 'isLoading'],
        placeholderData: (previousData) => (previousData?.id === itemId ? previousData : placeholderData),
        queryFn,
        queryKey,
        refetchInterval: (query) => getJobItemDetailsRefetchInterval(query.state.data?.profileGenerationInfo),
        staleTime: 60_000,
        throwOnError: false,
    });

const useAvailableLanguages = (): readonly LanguageCode[] => {
    const {
        applicationState: { customization },
    } = useApplicationContext();

    return customization.get('commonConfiguration').get('languages').unwrapOr([]);
};

export const useJobItemDetails = ({
    architectureId,
    enabled = true,
    includeComments,
    itemId,
    itemType,
    version,
}: {
    readonly architectureId: string;
    readonly enabled?: boolean;
    readonly includeComments?: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly version: string | undefined;
}): UseQueryResult<JobItemDetails, Error> => {
    const { readJobLevelDetails, readJobRoleDetails } = useApplicationFetchers();

    const availableLanguages = useAvailableLanguages();

    const getQueryKey = (): QueryKey => {
        if (itemType === 'job-role') {
            return jobArchitectureKeys.role(architectureId, itemId, version, includeComments);
        }
        if (itemType === 'job-level') {
            return jobArchitectureKeys.level(architectureId, itemId, version, includeComments);
        }

        throw new Error(`Invalid item type: ${itemType}`);
    };

    return useQuery<JobItemDetails, Error>(
        createJobItemDetailsQueryOptions({
            enabled: enabled === true,
            itemId,
            queryKey: getQueryKey(),
            queryFn: async ({ signal }) => {
                const result =
                    itemType === 'job-role'
                        ? await readJobRoleDetails({
                              includeComments,
                              jobArchitectureId: architectureId,
                              jobRoleId: itemId,
                              signal,
                              version,
                          })
                        : await readJobLevelDetails({
                              includeComments,
                              jobArchitectureId: architectureId,
                              jobLevelId: itemId,
                              signal,
                              version,
                          });

                if (result.isErr === true) {
                    throw result.error;
                }

                return patchMissingTranslations(result.value, availableLanguages);
            },
        }),
    );
};

// Remembers the last loaded details per item so a version switch keeps the old content visible while fetching.
const usePreviousDetails = <TDetails extends JobItemDetails>(
    architectureId: string,
): {
    readonly get: (itemId: string) => TDetails | undefined;
    readonly remember: (
        requests: readonly JobItemDetailsRequest[],
        queries: readonly UseQueryResult<TDetails, Error>[],
    ) => void;
} => {
    const previousDetails = useRef(new Map<string, TDetails>());

    return {
        get: (itemId) => previousDetails.current.get(`${architectureId}:${itemId}`),
        remember: useCallback(
            (requests, queries) => {
                requests.forEach(({ itemId }, index) => {
                    const details = queries[index]?.data;

                    if (!is.undefined(details)) {
                        previousDetails.current.set(`${architectureId}:${itemId}`, details);
                    }
                });
            },
            [architectureId],
        ),
    };
};

export const useJobLevelDetailsQueries = ({
    architectureId,
    levelRequests,
}: {
    readonly architectureId: string;
    readonly levelRequests: readonly JobItemDetailsRequest[];
}): readonly UseQueryResult<JobLevelDetails, Error>[] => {
    const { readJobLevelDetails } = useApplicationFetchers();
    const availableLanguages = useAvailableLanguages();
    const previousDetails = usePreviousDetails<JobLevelDetails>(architectureId);

    const queries = useQueries({
        queries: levelRequests.map(({ enabled = true, itemId, version }) =>
            createJobLevelDetailsListQueryOptions({
                enabled,
                itemId,
                placeholderData: previousDetails.get(itemId),
                queryKey: jobArchitectureKeys.level(architectureId, itemId, version),
                queryFn: async ({ signal }): Promise<JobLevelDetails> => {
                    const result = await readJobLevelDetails({
                        jobArchitectureId: architectureId,
                        jobLevelId: itemId,
                        signal,
                        version,
                    });

                    if (result.isErr === true) {
                        throw result.error;
                    }

                    return patchMissingTranslations(result.value, availableLanguages);
                },
            }),
        ),
    });

    useEffect(() => {
        previousDetails.remember(levelRequests, queries);
    }, [levelRequests, previousDetails.remember, queries]);

    return queries;
};

export const useJobRoleDetailsQueries = ({
    architectureId,
    roleRequests,
}: {
    readonly architectureId: string;
    readonly roleRequests: readonly JobItemDetailsRequest[];
}): readonly UseQueryResult<JobRoleDetails, Error>[] => {
    const { readJobRoleDetails } = useApplicationFetchers();
    const availableLanguages = useAvailableLanguages();
    const previousDetails = usePreviousDetails<JobRoleDetails>(architectureId);

    const queries = useQueries({
        queries: roleRequests.map(({ enabled = true, itemId, version }) =>
            createJobRoleDetailsListQueryOptions({
                enabled,
                itemId,
                placeholderData: previousDetails.get(itemId),
                queryKey: jobArchitectureKeys.role(architectureId, itemId, version),
                queryFn: async ({ signal }): Promise<JobRoleDetails> => {
                    const result = await readJobRoleDetails({
                        jobArchitectureId: architectureId,
                        jobRoleId: itemId,
                        signal,
                        version,
                    });

                    if (result.isErr === true) {
                        throw result.error;
                    }

                    return patchMissingTranslations(result.value, availableLanguages);
                },
            }),
        ),
    });

    useEffect(() => {
        previousDetails.remember(roleRequests, queries);
    }, [previousDetails.remember, queries, roleRequests]);

    return queries;
};
